// FileViewer.jsx - Standalone File Viewer Component
// สามารถนำไปใช้ในส่วนอื่นของระบบได้

import React, { useEffect, useState } from 'react';
import { Image, Button, Typography, Spin, Alert } from 'antd';
import { 
  FileTextOutlined, 
  DownloadOutlined, 
  FileOutlined 
} from '@ant-design/icons';
import api from '../utils/api';

const { Text } = Typography;

/**
 * FileViewer Component - แสดงไฟล์หลากหลายประเภท
 * 
 * @param {string} fileUrl - URL ของไฟล์ที่จะแสดง
 * @param {number} imageWidth - ความกว้างของรูปภาพ (default: 200)
 * @param {number} pdfHeight - ความสูงของ PDF viewer (default: 400)
 * @param {boolean} showDownload - แสดงปุ่มดาวน์โหลดหรือไม่ (default: true)
 * @param {string} className - CSS class เพิ่มเติม
 * @param {object} style - inline style เพิ่มเติม
 */
const FileViewer = ({ 
  fileUrl, 
  imageWidth = 200, 
  pdfHeight = 400,
  showDownload = true,
  className = '',
  style = {}
}) => {
  const [privateUrl, setPrivateUrl] = useState('');
  const [privateMime, setPrivateMime] = useState('');
  const [privateError, setPrivateError] = useState('');
  const isPrivateFile = /\/api\/files\/\d+\/content(?:[?#]|$)/i.test(fileUrl || '');
  useEffect(() => {
    if (!isPrivateFile) { setPrivateUrl(''); setPrivateMime(''); setPrivateError(''); return undefined; }
    let active = true; let objectUrl = '';
    setPrivateError('');
    const requestUrl = /^https?:\/\//i.test(fileUrl) ? fileUrl : (fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`);
    api.get(requestUrl, { responseType: 'blob' }).then(response => {
      if (!active) return; objectUrl = URL.createObjectURL(response.data); setPrivateUrl(objectUrl); setPrivateMime(response.data.type || '');
    }).catch(error => { if (active) setPrivateError(error.response?.data?.message || error.message || 'ไม่สามารถเปิดไฟล์ได้'); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [fileUrl, isPrivateFile]);
  if (!fileUrl) return null;
  if (isPrivateFile && privateError) return <Alert type="error" showIcon message="ไม่สามารถเปิดตัวอย่างเอกสาร" description={privateError} />;
  if (isPrivateFile && !privateUrl) return <Spin size="small" />;
  const effectiveUrl = privateUrl || fileUrl;

  // ตรวจสอบประเภทไฟล์จาก extension
  const getFileType = (url) => {
    if (privateMime.startsWith('image/')) return 'image';
    if (privateMime === 'application/pdf') return 'pdf';
    const extension = url.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
      return 'image';
    } else if (extension === 'pdf') {
      return 'pdf';
    } else if (['doc', 'docx'].includes(extension)) {
      return 'word';
    } else if (['xls', 'xlsx'].includes(extension)) {
      return 'excel';
    } else if (['mp4', 'avi', 'mov', 'wmv'].includes(extension)) {
      return 'video';
    } else if (['mp3', 'wav', 'aac', 'm4a'].includes(extension)) {
      return 'audio';
    }
    return 'unknown';
  };

  const fileType = getFileType(effectiveUrl);
  const fileName = fileUrl.split('/').pop();

  // แสดงไฟล์รูปภาพ
  if (fileType === 'image') {
    return (
      <div className={className} style={style}>
        <Image
          width={imageWidth}
          src={effectiveUrl}
          style={{ border: '1px solid #d9d9d9', borderRadius: '4px' }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYSFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhQ=="
        />
        {showDownload && (
          <div style={{ marginTop: 8 }}>
            <Button 
              type="link" 
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => window.open(effectiveUrl, '_blank')}
            >
              ดาวน์โหลด
            </Button>
          </div>
        )}
      </div>
    );
  }

  // แสดงไฟล์ PDF
  if (fileType === 'pdf') {
    return (
      <div className={className} style={{ border: '1px solid #d9d9d9', borderRadius: '4px', ...style }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #d9d9d9'
        }}>
          <Text strong>
            <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            ไฟล์ PDF: {fileName}
          </Text>
          <Button 
            type="link" 
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => window.open(effectiveUrl, '_blank')}
          >
            เปิดในแท็บใหม่
          </Button>
        </div>
        <iframe
          src={effectiveUrl}
          width="100%"
          height={pdfHeight}
          style={{ border: 'none' }}
          title="PDF Viewer"
        />
      </div>
    );
  }

  // แสดงไฟล์ประเภทอื่นๆ ที่ไม่รองรับการ preview
  const getFileIcon = (type) => {
    switch (type) {
      case 'word':
        return '📄';
      case 'excel':
        return '📊';
      case 'video':
        return '🎥';
      case 'audio':
        return '🎵';
      default:
        return <FileOutlined style={{ fontSize: '24px', color: '#8c8c8c' }} />;
    }
  };

  const getFileDescription = (type) => {
    switch (type) {
      case 'word':
        return 'เอกสาร Microsoft Word';
      case 'excel':
        return 'ตารางคำนวณ Microsoft Excel';
      case 'video':
        return 'ไฟล์วิดีโอ';
      case 'audio':
        return 'ไฟล์เสียง';
      default:
        return 'ไฟล์เอกสาร';
    }
  };

  return (
    <div className={className} style={{ 
      padding: '20px', 
      textAlign: 'center', 
      border: '1px dashed #d9d9d9',
      borderRadius: '4px',
      backgroundColor: '#fafafa',
      ...style
    }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>
        {getFileIcon(fileType)}
      </div>
      <div>
        <Text strong>{getFileDescription(fileType)}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {fileName}
        </Text>
        <br />
        {showDownload && (
          <Button 
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => window.open(effectiveUrl, '_blank')}
            style={{ marginTop: 8 }}
          >
            ดาวน์โหลดไฟล์
          </Button>
        )}
      </div>
    </div>
  );
};

export default FileViewer;

/* 
การใช้งาน:

import FileViewer from '../components/FileViewer';

// แสดงรูปภาพ
<FileViewer fileUrl="/uploads/images/receipt.jpg" />

// แสดง PDF
<FileViewer 
  fileUrl="/uploads/documents/invoice.pdf" 
  pdfHeight={500}
/>

// แสดงไฟล์อื่นๆ
<FileViewer 
  fileUrl="/uploads/docs/report.docx"
  showDownload={true}
/>

// กำหนด style เพิ่มเติม
<FileViewer 
  fileUrl="/uploads/files/data.xlsx"
  style={{ maxWidth: 400 }}
  className="custom-file-viewer"
/>
*/
